/**
 * DateTimePicker — custom modal with a mini calendar + time wheels.
 * No external dependencies — uses luxon for date math.
 */

import React, { useState, useMemo, useCallback, useEffect, useId } from 'react';
import {
  View,
  Text,
  Modal,
  Pressable,
  TouchableOpacity,
  StyleSheet,
  useWindowDimensions,
  PanResponder,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import { DateTime } from 'luxon';
import { useTheme, ThemeColors } from '../../utils/theme';
import { FONT_SIZE, SPACING, RADIUS, SHADOW } from '../../utils/design-tokens';
import { useBackHandler } from '../../navigation/useBackHandler';

type LuxonDateTime = ReturnType<typeof DateTime.local>;

// ── Props ───────────────────────────────────────────────────
interface Props {
  visible: boolean;
  value: string;          // ISO 8601 string (UTC)
  onConfirm: (iso: string) => void;
  onCancel: () => void;
  title?: string;
  minDate?: string;       // optional ISO minimum date
}

// ── Constants ───────────────────────────────────────────────
const WEEKDAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const HOURS_12 = Array.from({ length: 12 }, (_, i) => i + 1); // 1–12
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);  // 0,5,10…55

// ── Component ───────────────────────────────────────────────
export default function DateTimePicker({ visible, value, onConfirm, onCancel, title, minDate }: Props) {
  const { colors } = useTheme();
  const s = useMemo(() => createStyles(colors), [colors]);
  const { width: screenWidth } = useWindowDimensions();
  const modalId = useId();

  useBackHandler({
    id: `date-time-picker-${modalId}`,
    label: title || 'Date time picker',
    priority: 950,
    enabled: visible,
    onBack: () => {
      onCancel();
      return true;
    },
  });

  // Parse initial value into local date/time parts
  const initial = useMemo(() => {
    const dt = DateTime.fromISO(value).toLocal();
    if (!dt.isValid) return { date: DateTime.local(), hour12: 12, minute: 0, ampm: 'AM' as const };
    const h24 = dt.hour;
    return {
      date: dt,
      hour12: h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24,
      minute: Math.round(dt.minute / 5) * 5, // snap to nearest 5
      ampm: (h24 < 12 ? 'AM' : 'PM') as 'AM' | 'PM',
    };
  }, [value]);

  const [viewMonth, setViewMonth] = useState(() => initial.date.startOf('month'));
  const [selectedDate, setSelectedDate] = useState(initial.date.startOf('day'));
  const [hour12, setHour12] = useState(initial.hour12);
  const [minute, setMinute] = useState(initial.minute >= 60 ? 55 : initial.minute);
  const [ampm, setAmpm] = useState<'AM' | 'PM'>(initial.ampm);

  useEffect(() => {
    if (!visible) return;
    setViewMonth(initial.date.startOf('month'));
    setSelectedDate(initial.date.startOf('day'));
    setHour12(initial.hour12);
    setMinute(initial.minute >= 60 ? 55 : initial.minute);
    setAmpm(initial.ampm);
  }, [initial, visible]);

  // Compute calendar grid for viewMonth
  const calendarWeeks = useMemo(() => {
    const first = viewMonth.startOf('month');
    // luxon weekday: 1 = Monday … 7 = Sunday
    const startOffset = first.weekday - 1; // 0-based from Monday
    const daysInMonth = viewMonth.daysInMonth || 30;
    const cells: (LuxonDateTime | null)[] = [];

    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push(DateTime.local(viewMonth.year, viewMonth.month, d));
    }
    while (cells.length % 7 !== 0) cells.push(null);

    const weeks: (LuxonDateTime | null)[][] = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
    return weeks;
  }, [viewMonth]);

  const monthLabel = viewMonth.toFormat('LLLL yyyy');
  const today = DateTime.local().startOf('day');

  const minDt = useMemo(() => {
    if (!minDate) return null;
    const dt = DateTime.fromISO(minDate).toLocal().startOf('day');
    return dt.isValid ? dt : null;
  }, [minDate]);

  const changeMonth = useCallback((dir: -1 | 1) => {
    Haptics.selectionAsync();
    setViewMonth((m: LuxonDateTime) => m.plus({ months: dir }));
  }, []);

  const canSelectToday = !minDt || today >= minDt;

  const goToToday = useCallback(() => {
    const currentToday = DateTime.local().startOf('day');
    Haptics.selectionAsync();
    setViewMonth(currentToday.startOf('month'));
    if (!minDt || currentToday >= minDt) {
      setSelectedDate(currentToday);
    }
  }, [minDt]);

  const monthSwipeResponder = useMemo(
    () => PanResponder.create({
      onMoveShouldSetPanResponder: (_evt, gestureState) => (
        Math.abs(gestureState.dx) > 12 && Math.abs(gestureState.dx) > Math.abs(gestureState.dy)
      ),
      onPanResponderRelease: (_evt, gestureState) => {
        if (gestureState.dx <= -40) changeMonth(1);
        else if (gestureState.dx >= 40) changeMonth(-1);
      },
      onPanResponderTerminate: (_evt, gestureState) => {
        if (gestureState.dx <= -40) changeMonth(1);
        else if (gestureState.dx >= 40) changeMonth(-1);
      },
    }),
    [changeMonth]
  );

  const selectDay = useCallback((day: LuxonDateTime) => {
    Haptics.selectionAsync();
    setSelectedDate(day.startOf('day'));
  }, []);

  const cycleHour = useCallback((dir: 1 | -1) => {
    Haptics.selectionAsync();
    setHour12((h: number) => {
      const next = h + dir;
      if (next > 12) return 1;
      if (next < 1) return 12;
      return next;
    });
  }, []);

  const cycleMinute = useCallback((dir: 1 | -1) => {
    Haptics.selectionAsync();
    setMinute((m: number) => {
      const next = m + dir * 5;
      if (next >= 60) return 0;
      if (next < 0) return 55;
      return next;
    });
  }, []);

  const toggleAmPm = useCallback(() => {
    Haptics.selectionAsync();
    setAmpm((v: 'AM' | 'PM') => (v === 'AM' ? 'PM' : 'AM'));
  }, []);

  const handleConfirm = useCallback(() => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    // Build 24h hour
    let h24 = hour12 % 12; // 12 → 0
    if (ampm === 'PM') h24 += 12;
    const result = selectedDate.set({ hour: h24, minute, second: 0, millisecond: 0 }).toUTC().toISO();
    onConfirm(result!);
  }, [selectedDate, hour12, minute, ampm, onConfirm]);

  const handleCancel = useCallback(() => {
    Haptics.selectionAsync();
    onCancel();
  }, [onCancel]);

  const cellSize = Math.min(38, (screenWidth - 80) / 7);

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={handleCancel}>
      <Pressable style={s.backdrop} onPress={handleCancel}>
        <Pressable style={s.card} onPress={() => {}}>
          {/* Title */}
          {title && <Text style={s.title}>{title}</Text>}

          {/* ─── Calendar Section ─── */}
          <View style={s.calendarHeader}>
            <TouchableOpacity onPress={() => changeMonth(-1)} style={s.monthArrow} accessibilityRole="button" accessibilityLabel="Previous month">
              <Ionicons name="chevron-back" size={18} color={colors.primary} />
            </TouchableOpacity>
            <Text style={s.monthLabel}>{monthLabel}</Text>
            <TouchableOpacity onPress={() => changeMonth(1)} style={s.monthArrow} accessibilityRole="button" accessibilityLabel="Next month">
              <Ionicons name="chevron-forward" size={18} color={colors.primary} />
            </TouchableOpacity>
          </View>

          <View style={s.calendarShortcutRow}>
            <TouchableOpacity
              onPress={goToToday}
              disabled={!canSelectToday}
              style={[s.todayShortcut, !canSelectToday && s.todayShortcutDisabled]}
              accessibilityRole="button"
              accessibilityLabel="Go to today"
            >
              <Ionicons name="today-outline" size={14} color={canSelectToday ? colors.primary : colors.textSecondary} />
              <Text style={[s.todayShortcutText, !canSelectToday && s.todayShortcutTextDisabled]}>Today</Text>
            </TouchableOpacity>
          </View>

          <View {...monthSwipeResponder.panHandlers}>
            {/* Weekday headers */}
            <View style={s.weekdayRow}>
              {WEEKDAY_LABELS.map((d, i) => (
                <Text key={i} style={[s.weekdayLabel, { width: cellSize }]}>{d}</Text>
              ))}
            </View>

            {/* Day grid */}
            {calendarWeeks.map((week, wi) => (
              <View key={wi} style={s.weekRow}>
                {week.map((day, di) => {
                  if (!day) return <View key={di} style={{ width: cellSize, height: cellSize }} />;
                  const isSelected = day.hasSame(selectedDate, 'day');
                  const isToday = day.hasSame(today, 'day');
                  const isDisabled = minDt ? day < minDt : false;
                  return (
                    <TouchableOpacity
                      key={di}
                      onPress={() => !isDisabled && selectDay(day)}
                      disabled={isDisabled}
                      style={[
                        s.dayCell,
                        { width: cellSize, height: cellSize, borderRadius: cellSize / 2 },
                        isToday && !isSelected && s.dayCellToday,
                        isSelected && !isToday && s.dayCellSelected,
                        isSelected && isToday && s.dayCellTodaySelected,
                      ]}
                    >
                      <Text style={[
                        s.dayText,
                        isToday && !isSelected && s.dayTextToday,
                        isSelected && !isToday && s.dayTextSelected,
                        isSelected && isToday && s.dayTextTodaySelected,
                        isDisabled && s.dayTextDisabled,
                      ]}>
                        {day.day}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            ))}
          </View>

          {/* ─── Time Section ─── */}
          <View style={s.timeDivider} />
          <View style={s.timeSection}>
            {/* Hour */}
            <View style={s.wheelCol}>
              <TouchableOpacity onPress={() => cycleHour(1)} style={s.wheelArrow}>
                <Ionicons name="chevron-up" size={20} color={colors.primary} />
              </TouchableOpacity>
              <Text style={s.wheelValue}>{String(hour12).padStart(2, '0')}</Text>
              <TouchableOpacity onPress={() => cycleHour(-1)} style={s.wheelArrow}>
                <Ionicons name="chevron-down" size={20} color={colors.primary} />
              </TouchableOpacity>
            </View>

            <Text style={s.timeSep}>:</Text>

            {/* Minute */}
            <View style={s.wheelCol}>
              <TouchableOpacity onPress={() => cycleMinute(1)} style={s.wheelArrow}>
                <Ionicons name="chevron-up" size={20} color={colors.primary} />
              </TouchableOpacity>
              <Text style={s.wheelValue}>{String(minute).padStart(2, '0')}</Text>
              <TouchableOpacity onPress={() => cycleMinute(-1)} style={s.wheelArrow}>
                <Ionicons name="chevron-down" size={20} color={colors.primary} />
              </TouchableOpacity>
            </View>

            {/* AM/PM */}
            <TouchableOpacity onPress={toggleAmPm} style={s.ampmButton}>
              <Text style={s.ampmText}>{ampm}</Text>
            </TouchableOpacity>
          </View>

          {/* ─── Buttons ─── */}
          <View style={s.buttonRow}>
            <TouchableOpacity onPress={handleCancel} style={s.cancelBtn}>
              <Text style={s.cancelBtnText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={handleConfirm} style={s.confirmBtn}>
              <Text style={s.confirmBtnText}>Confirm</Text>
            </TouchableOpacity>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

// ── Styles ──────────────────────────────────────────────────
const createStyles = (colors: ThemeColors) => StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(15, 23, 42, 0.28)',
    justifyContent: 'center',
    padding: 20,
  },
  card: {
    backgroundColor: colors.card,
    borderRadius: RADIUS.xl,
    padding: SPACING.lg,
    ...SHADOW.elevated,
  },
  title: {
    fontSize: FONT_SIZE.base,
    fontWeight: '700',
    color: colors.text,
    textAlign: 'center',
    marginBottom: SPACING.md,
  },

  // Calendar
  calendarHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: SPACING.md,
  },
  monthArrow: {
    padding: 6,
  },
  monthLabel: {
    fontSize: FONT_SIZE.base,
    fontWeight: '600',
    color: colors.text,
  },
  calendarShortcutRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: SPACING.sm,
  },
  todayShortcut: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
    paddingHorizontal: SPACING.md,
    paddingVertical: 6,
    borderRadius: RADIUS.full,
    backgroundColor: colors.primaryLight,
    borderWidth: 1,
    borderColor: colors.border,
  },
  todayShortcutDisabled: {
    opacity: 0.45,
  },
  todayShortcutText: {
    fontSize: FONT_SIZE.sm,
    fontWeight: '700',
    color: colors.primary,
  },
  todayShortcutTextDisabled: {
    color: colors.textSecondary,
  },
  weekdayRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginBottom: SPACING.xs,
  },
  weekdayLabel: {
    textAlign: 'center',
    fontSize: FONT_SIZE.xs,
    fontWeight: '600',
    color: colors.textSecondary,
  },
  weekRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginBottom: 2,
  },
  dayCell: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  dayCellToday: {
    borderWidth: 1.5,
    borderColor: colors.error,
    backgroundColor: colors.error + '08',
  },
  dayCellSelected: {
    backgroundColor: colors.primary,
  },
  dayCellTodaySelected: {
    borderWidth: 1.5,
    borderColor: colors.error,
    backgroundColor: colors.error,
  },
  dayText: {
    fontSize: FONT_SIZE.sm,
    color: colors.text,
    fontWeight: '500',
  },
  dayTextToday: {
    color: colors.error,
    fontWeight: '800',
  },
  dayTextSelected: {
    color: '#fff',
    fontWeight: '700',
  },
  dayTextTodaySelected: {
    color: '#fff',
    fontWeight: '800',
  },
  dayTextDisabled: {
    color: colors.border,
  },

  // Time
  timeDivider: {
    height: 1,
    backgroundColor: colors.border,
    marginVertical: SPACING.md,
  },
  timeSection: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: SPACING.sm,
    marginBottom: SPACING.lg,
  },
  wheelCol: {
    alignItems: 'center',
    width: 56,
  },
  wheelArrow: {
    padding: 4,
  },
  wheelValue: {
    fontSize: 28,
    fontWeight: '300',
    color: colors.text,
    letterSpacing: 1,
    paddingVertical: 4,
  },
  timeSep: {
    fontSize: 28,
    fontWeight: '300',
    color: colors.textSecondary,
    marginBottom: 2,
  },
  ampmButton: {
    marginLeft: SPACING.sm,
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.sm,
    borderRadius: RADIUS.md,
    backgroundColor: colors.primaryLight,
  },
  ampmText: {
    fontSize: FONT_SIZE.base,
    fontWeight: '700',
    color: colors.primary,
  },

  // Buttons
  buttonRow: {
    flexDirection: 'row',
    gap: SPACING.md,
  },
  cancelBtn: {
    flex: 1,
    paddingVertical: SPACING.md,
    borderRadius: RADIUS.lg,
    backgroundColor: colors.primaryLight,
    alignItems: 'center',
  },
  cancelBtnText: {
    fontSize: FONT_SIZE.base,
    fontWeight: '600',
    color: colors.text,
  },
  confirmBtn: {
    flex: 1,
    paddingVertical: SPACING.md,
    borderRadius: RADIUS.lg,
    backgroundColor: colors.primary,
    alignItems: 'center',
  },
  confirmBtnText: {
    fontSize: FONT_SIZE.base,
    fontWeight: '700',
    color: '#fff',
  },
});
